import React, { useEffect, useRef, useState } from "react";
import { useContextStateManagement } from "../context/ContextApi";
import "./ToolDefinitionsPage.css";

// New standalone "Tools" page (Issue 10 of the tool-definitions version-control PRD): a
// per-practice draft -> submitted -> published -> archived lifecycle for the same three tool
// types the existing /api-integration page (ApiIntegration.js + FunctionModal.js) edits with
// immediate-persist saves per individual row - here, every apiFunction / cold_transfer /
// warm_transfer definition for the practice versions together as ONE atomic bundle instead.
// This page is intentionally separate from /api-integration (left completely untouched) for a
// side-by-side coexistence period, mirroring how Agent Config coexists with Prompt Hub. There is
// no AI-suggestions gate on Publish here (out of scope for tool configs per the PRD) and Publish
// does not yet move what a live call actually uses - Issue 11 extends this page's Publish action
// to also write the shared live-pointer (config_type='tools'), and Issue 13 makes the legacy
// /api-integration page's own save keep "last write wins" with this one.

const AUTOSAVE_DELAY_MS = 1200;

const FUNC_TYPE_OPTIONS = [
  { value: "apiFunction", label: "API Call" },
  { value: "cold_transfer", label: "Cold Transfer" },
  { value: "warm_transfer", label: "Warm Transfer" },
];

const FUNC_TYPE_LABELS = FUNC_TYPE_OPTIONS.reduce(
  (result, option) => ({ ...result, [option.value]: option.label }),
  {},
);

let toolIdCounter = 0;
function generateToolId() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  toolIdCounter += 1;
  return `tool-${Date.now()}-${toolIdCounter}`;
}

function emptyToolDraft(funcType = "apiFunction") {
  return {
    id: null,
    funcType,
    name: "",
    description: "",
    url: "",
    method: "GET",
    authHeader: "",
    executionMode: "standard",
    timeoutSec: 60,
    retryCount: 0,
    attributes: [],
    routingRules: [],
    handoverPhoneNumber: "",
    extension: "",
    transferLine: "",
    warmSummaryPrompt: "",
  };
}

// Normalizes one tool entry coming back from the draft/submitted/history endpoints so every
// field is always defined with the right primitive type, regardless of which fields the backend
// bundle actually had set.
function normalizeTool(raw) {
  const safe = raw || {};
  const funcType = FUNC_TYPE_LABELS[safe.funcType] ? safe.funcType : "apiFunction";
  return {
    id: typeof safe.id === "string" && safe.id ? safe.id : generateToolId(),
    funcType,
    name: typeof safe.name === "string" ? safe.name : "",
    description: typeof safe.description === "string" ? safe.description : "",
    url: typeof safe.url === "string" ? safe.url : "",
    method: typeof safe.method === "string" && safe.method ? safe.method : "GET",
    authHeader: typeof safe.authHeader === "string" ? safe.authHeader : "",
    executionMode: safe.executionMode === "stateful" ? "stateful" : "standard",
    timeoutSec: Number.isFinite(Number(safe.timeoutSec)) ? Number(safe.timeoutSec) : 60,
    retryCount: Number.isFinite(Number(safe.retryCount)) ? Number(safe.retryCount) : 0,
    attributes: Array.isArray(safe.attributes)
      ? safe.attributes.map((attr) => ({
          name: typeof attr?.name === "string" ? attr.name : "",
          attrType: typeof attr?.attrType === "string" && attr.attrType ? attr.attrType : "string",
          required: Boolean(attr?.required),
          defaultValue: typeof attr?.defaultValue === "string" ? attr.defaultValue : "",
        }))
      : [],
    routingRules: Array.isArray(safe.routingRules)
      ? safe.routingRules.map((rule) => ({
          field: typeof rule?.field === "string" ? rule.field : "",
          min: typeof rule?.min === "string" ? rule.min : rule?.min ?? "",
          max: typeof rule?.max === "string" ? rule.max : rule?.max ?? "",
          targetUrl: typeof rule?.targetUrl === "string" ? rule.targetUrl : "",
          targetAuthHeader:
            typeof rule?.targetAuthHeader === "string" ? rule.targetAuthHeader : "",
        }))
      : [],
    handoverPhoneNumber:
      typeof safe.handoverPhoneNumber === "string" ? safe.handoverPhoneNumber : "",
    extension: typeof safe.extension === "string" ? safe.extension : "",
    transferLine: typeof safe.transferLine === "string" ? safe.transferLine : "",
    warmSummaryPrompt: typeof safe.warmSummaryPrompt === "string" ? safe.warmSummaryPrompt : "",
  };
}

function normalizeBundle(bundle) {
  const safeBundle = bundle || {};
  return {
    tools: Array.isArray(safeBundle.tools) ? safeBundle.tools.map(normalizeTool) : [],
  };
}

// Builds the exact JSON persisted as one atomic bundle - strips the type-specific fields that
// don't apply to a given tool's funcType so drafts stay tidy (e.g. a cold_transfer entry never
// carries a leftover `url` from when it was briefly toggled to apiFunction in the editor).
function buildTool(tool) {
  const base = {
    id: tool.id,
    funcType: tool.funcType,
    name: tool.name,
    description: tool.description,
  };

  if (tool.funcType === "apiFunction") {
    return {
      ...base,
      url: tool.url,
      method: tool.method,
      authHeader: tool.authHeader,
      executionMode: tool.executionMode,
      timeoutSec: tool.timeoutSec,
      retryCount: tool.retryCount,
      attributes: tool.attributes.map((attr) => ({ ...attr })),
      routingRules: tool.routingRules.map((rule) => ({ ...rule })),
    };
  }

  return {
    ...base,
    handoverPhoneNumber: tool.handoverPhoneNumber,
    extension: tool.extension,
    transferLine: tool.transferLine,
    ...(tool.funcType === "warm_transfer"
      ? { warmSummaryPrompt: tool.warmSummaryPrompt }
      : {}),
  };
}

function buildBundle(tools) {
  return { tools: tools.map(buildTool) };
}

function serializeSnapshot(tools) {
  return JSON.stringify(buildBundle(tools));
}

function formatSavedAt(value) {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}

// Short single-line preview of a history entry's bundle - just enough to recognize which
// version is which without opening it.
function summarizeBundleForHistory(bundle) {
  const tools = Array.isArray(bundle?.tools) ? bundle.tools : [];
  if (tools.length === 0) return "(empty bundle)";
  const counts = tools.reduce((acc, tool) => {
    const label = FUNC_TYPE_LABELS[tool?.funcType] || "Unknown";
    acc[label] = (acc[label] || 0) + 1;
    return acc;
  }, {});
  return Object.entries(counts)
    .map(([label, count]) => `${count} ${label}${count === 1 ? "" : "s"}`)
    .join(", ");
}

export const ToolDefinitionsPage = () => {
  const {
    PracticeId,
    getToolDefinitionsDraft,
    saveToolDefinitionsDraft,
    submitToolDefinitionsDraft,
    getSubmittedToolDefinitions,
    publishToolDefinitionsDraft,
    getToolDefinitionsHistory,
    rollbackToolDefinitionsVersion,
  } = useContextStateManagement();

  const [activePageTab, setActivePageTab] = useState("editor");
  const [tools, setTools] = useState([]);

  const [loading, setLoading] = useState(true);
  const [initialLoadDone, setInitialLoadDone] = useState(false);
  const [saveState, setSaveState] = useState("idle");
  const [errorMessage, setErrorMessage] = useState("");
  const [lastSavedAt, setLastSavedAt] = useState(null);

  const [submitState, setSubmitState] = useState("idle");
  const [submitErrorMessage, setSubmitErrorMessage] = useState("");
  const [lastSubmittedAt, setLastSubmittedAt] = useState(null);
  const [submittedSnapshotJson, setSubmittedSnapshotJson] = useState(null);

  const [publishState, setPublishState] = useState("idle");
  const [publishMessage, setPublishMessage] = useState("");

  const [historyVersions, setHistoryVersions] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyErrorMessage, setHistoryErrorMessage] = useState("");
  const [rollbackTargetVersion, setRollbackTargetVersion] = useState(null);
  const [rollbackState, setRollbackState] = useState("idle");
  const [rollbackErrorMessage, setRollbackErrorMessage] = useState("");

  // Add/edit-tool modal state. `editingToolId` is null while adding a brand-new tool.
  const [showToolModal, setShowToolModal] = useState(false);
  const [editingToolId, setEditingToolId] = useState(null);
  const [toolDraft, setToolDraft] = useState(emptyToolDraft());
  const [toolModalError, setToolModalError] = useState("");

  const lastSavedSnapshotRef = useRef(serializeSnapshot([]));
  const debounceRef = useRef(null);
  const saveStateRef = useRef("idle");
  const toolsRef = useRef([]);

  useEffect(() => {
    saveStateRef.current = saveState;
  }, [saveState]);

  useEffect(() => {
    toolsRef.current = tools;
  }, [tools]);

  const persistBundle = async (currentTools) => {
    if (!PracticeId) return;
    setSaveState("saving");
    try {
      const response = await saveToolDefinitionsDraft(PracticeId, buildBundle(currentTools));
      lastSavedSnapshotRef.current = serializeSnapshot(currentTools);
      setSaveState("saved");
      setLastSavedAt(response?.updatedAt || new Date().toISOString());
      setErrorMessage("");
    } catch (error) {
      setSaveState("error");
      setErrorMessage(
        error?.message ||
          "Couldn't save the draft. Your changes are kept locally - try Save Draft again.",
      );
    }
  };

  const refreshHistory = async () => {
    if (!PracticeId) return;
    setHistoryLoading(true);
    try {
      const response = await getToolDefinitionsHistory(PracticeId);
      setHistoryVersions(Array.isArray(response?.versions) ? response.versions : []);
      setHistoryErrorMessage("");
    } catch (error) {
      setHistoryErrorMessage(error?.message || "Couldn't load version history. Try again.");
    } finally {
      setHistoryLoading(false);
    }
  };

  useEffect(() => {
    if (!PracticeId) {
      setLoading(false);
      return undefined;
    }

    let cancelled = false;
    setLoading(true);
    setInitialLoadDone(false);
    setErrorMessage("");

    Promise.all([
      getToolDefinitionsDraft(PracticeId),
      getSubmittedToolDefinitions(PracticeId).catch(() => null),
      getToolDefinitionsHistory(PracticeId).catch(() => null),
    ])
      .then(([draft, submitted, historyResponse]) => {
        if (cancelled) return;

        const submittedBundle = submitted?.exists ? normalizeBundle(submitted.bundle) : null;
        const startingBundle = draft?.exists
          ? normalizeBundle(draft.bundle)
          : submittedBundle || normalizeBundle({});

        setTools(startingBundle.tools);
        lastSavedSnapshotRef.current = serializeSnapshot(startingBundle.tools);
        setLastSavedAt(draft?.updatedAt || submitted?.submittedAt || null);

        setSubmittedSnapshotJson(
          submittedBundle ? serializeSnapshot(submittedBundle.tools) : null,
        );
        setLastSubmittedAt(submitted?.submittedAt || null);

        setHistoryVersions(
          Array.isArray(historyResponse?.versions) ? historyResponse.versions : [],
        );

        setSaveState("idle");
      })
      .catch((error) => {
        if (cancelled) return;
        setErrorMessage(error?.message || "Failed to load the saved draft.");
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
        setInitialLoadDone(true);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [PracticeId]);

  // Autosave: debounce edits to the tool list, only once the initial load has finished.
  useEffect(() => {
    if (!initialLoadDone || !PracticeId) return undefined;

    if (serializeSnapshot(tools) === lastSavedSnapshotRef.current) {
      return undefined;
    }

    setSaveState("unsaved");
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      persistBundle(tools);
    }, AUTOSAVE_DELAY_MS);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tools, initialLoadDone, PracticeId]);

  // Best-effort flush of a pending edit if the user navigates away before the debounce fires.
  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      if (saveStateRef.current === "unsaved" || saveStateRef.current === "saving") {
        persistBundle(toolsRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSaveNow = () => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    persistBundle(tools);
  };

  const handleSubmitDraft = async () => {
    if (!PracticeId) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);

    setSubmitState("submitting");
    setSubmitErrorMessage("");
    try {
      const response = await submitToolDefinitionsDraft(PracticeId, buildBundle(tools));
      const submittedJson = serializeSnapshot(tools);

      lastSavedSnapshotRef.current = submittedJson;
      setSaveState("saved");
      setLastSavedAt(response?.submittedAt || new Date().toISOString());

      setSubmittedSnapshotJson(submittedJson);
      setLastSubmittedAt(response?.submittedAt || new Date().toISOString());
      setSubmitState("idle");
      setPublishMessage("");
    } catch (error) {
      setSubmitState("error");
      setSubmitErrorMessage(error?.message || "Couldn't submit the draft. Try again.");
    }
  };

  const handlePublishClick = async () => {
    if (!PracticeId || !publishEnabled) return;
    setPublishState("publishing");
    setPublishMessage("");
    try {
      const response = await publishToolDefinitionsDraft(PracticeId);

      setSubmittedSnapshotJson(null);
      setLastSubmittedAt(null);

      const publishedAtLabel = formatSavedAt(response?.publishedAt);
      setPublishMessage(
        `Published${response?.versionId ? ` as version ${response.versionId}` : ""}${
          publishedAtLabel ? ` - ${publishedAtLabel}` : ""
        }.`,
      );
      setPublishState("idle");

      await refreshHistory();
    } catch (error) {
      setPublishState("error");
      setPublishMessage(error?.message || "Couldn't publish this bundle. Try again.");
    }
  };

  const handleOpenHistoryTab = () => {
    setActivePageTab("history");
    refreshHistory();
  };

  const handleRequestRollback = (version) => {
    setRollbackErrorMessage("");
    setRollbackTargetVersion(version);
  };

  const handleCancelRollback = () => {
    setRollbackTargetVersion(null);
  };

  const handleConfirmRollback = async () => {
    if (!PracticeId || !rollbackTargetVersion) return;
    setRollbackState("rolling-back");
    setRollbackErrorMessage("");
    try {
      await rollbackToolDefinitionsVersion(PracticeId, rollbackTargetVersion.versionId);
      setRollbackTargetVersion(null);
      setRollbackState("idle");
      await refreshHistory();
    } catch (error) {
      setRollbackState("error");
      setRollbackErrorMessage(error?.message || "Couldn't roll back to that version. Try again.");
    }
  };

  // --- Add/edit-tool modal -------------------------------------------------------------------

  const openAddToolModal = () => {
    setEditingToolId(null);
    setToolDraft(emptyToolDraft());
    setToolModalError("");
    setShowToolModal(true);
  };

  const openEditToolModal = (tool) => {
    setEditingToolId(tool.id);
    setToolDraft({ ...tool, attributes: [...tool.attributes], routingRules: [...tool.routingRules] });
    setToolModalError("");
    setShowToolModal(true);
  };

  const closeToolModal = () => {
    setShowToolModal(false);
    setEditingToolId(null);
    setToolModalError("");
  };

  const handleToolFieldChange = (field, value) => {
    setToolDraft((current) => ({ ...current, [field]: value }));
  };

  const handleAddAttribute = () => {
    setToolDraft((current) => ({
      ...current,
      attributes: [
        ...current.attributes,
        { name: "", attrType: "string", required: false, defaultValue: "" },
      ],
    }));
  };

  const handleAttributeChange = (index, field, value) => {
    setToolDraft((current) => ({
      ...current,
      attributes: current.attributes.map((attr, i) =>
        i === index ? { ...attr, [field]: value } : attr,
      ),
    }));
  };

  const handleRemoveAttribute = (index) => {
    setToolDraft((current) => ({
      ...current,
      attributes: current.attributes.filter((_, i) => i !== index),
    }));
  };

  const handleAddRoutingRule = () => {
    setToolDraft((current) => ({
      ...current,
      routingRules: [
        ...current.routingRules,
        { field: "", min: "", max: "", targetUrl: "", targetAuthHeader: "" },
      ],
    }));
  };

  const handleRoutingRuleChange = (index, field, value) => {
    setToolDraft((current) => ({
      ...current,
      routingRules: current.routingRules.map((rule, i) =>
        i === index ? { ...rule, [field]: value } : rule,
      ),
    }));
  };

  const handleRemoveRoutingRule = (index) => {
    setToolDraft((current) => ({
      ...current,
      routingRules: current.routingRules.filter((_, i) => i !== index),
    }));
  };

  const handleSaveToolFromModal = () => {
    if (!toolDraft.name.trim()) {
      setToolModalError("Name is required.");
      return;
    }
    if (toolDraft.funcType === "apiFunction" && !toolDraft.url.trim()) {
      setToolModalError("URL is required for an API call tool.");
      return;
    }
    if (
      (toolDraft.funcType === "cold_transfer" || toolDraft.funcType === "warm_transfer") &&
      !toolDraft.handoverPhoneNumber.trim()
    ) {
      setToolModalError("A handover phone number is required for a transfer tool.");
      return;
    }

    const finalizedTool = {
      ...toolDraft,
      id: toolDraft.id || generateToolId(),
    };

    setTools((current) => {
      if (editingToolId) {
        return current.map((tool) => (tool.id === editingToolId ? finalizedTool : tool));
      }
      return [...current, finalizedTool];
    });

    closeToolModal();
  };

  const handleRemoveTool = (id) => {
    setTools((current) => current.filter((tool) => tool.id !== id));
  };

  const savedAtLabel = formatSavedAt(lastSavedAt);
  const statusMeta = {
    idle: { label: "No changes yet", className: "tool-defs-status--idle" },
    unsaved: { label: "Unsaved changes", className: "tool-defs-status--unsaved" },
    saving: { label: "Saving...", className: "tool-defs-status--saving" },
    saved: {
      label: savedAtLabel ? `All changes saved - ${savedAtLabel}` : "All changes saved",
      className: "tool-defs-status--saved",
    },
    error: { label: "Couldn't save - will retry", className: "tool-defs-status--error" },
  }[saveState] || { label: "", className: "" };

  const currentSnapshotJson = serializeSnapshot(tools);
  const hasSubmission = submittedSnapshotJson !== null;
  const isDirtySinceSubmit = hasSubmission && currentSnapshotJson !== submittedSnapshotJson;
  const lastSubmittedAtLabel = formatSavedAt(lastSubmittedAt);

  const publishEnabled = hasSubmission && publishState !== "publishing";
  const publishTitle = !hasSubmission
    ? "Submit a draft first to enable Publish"
    : publishState === "publishing"
      ? "Publishing..."
      : "Ready to publish";

  // Provisional "most recent publish" marker - there is no live-pointer resolve wired up to
  // this page yet (Issue 11 extends the shared ConfigLivePointers pointer to config_type='tools'
  // on top of this same Publish endpoint), so this only disables rolling a version back onto
  // itself rather than claiming to know what a live call actually resolves right now.
  const mostRecentPublishedVersionId = historyVersions[0]?.versionId ?? null;

  if (!PracticeId) {
    return (
      <div className="tool-defs-page p-4 text-light">
        Select or load a practice to edit its tool definitions.
      </div>
    );
  }

  return (
    <div className="tool-defs-page p-4">
      <div className="tool-defs-header">
        <h2 className="tool-defs-title">Tools</h2>
        <p className="tool-defs-subtitle">
          Edit this practice&apos;s API-call, cold-transfer, and warm-transfer tool definitions
          as one draft bundle. Nothing here affects real calls until it is published - separate
          from the existing API Integration page, which still saves immediately.
        </p>
      </div>

      <div className="tool-defs-page-tabs" role="tablist">
        <button
          type="button"
          role="tab"
          aria-selected={activePageTab === "editor"}
          className={`tool-defs-page-tab ${
            activePageTab === "editor" ? "tool-defs-page-tab--active" : ""
          }`}
          onClick={() => setActivePageTab("editor")}
        >
          Editor
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={activePageTab === "history"}
          className={`tool-defs-page-tab ${
            activePageTab === "history" ? "tool-defs-page-tab--active" : ""
          }`}
          onClick={handleOpenHistoryTab}
        >
          History{historyVersions.length ? ` (${historyVersions.length})` : ""}
        </button>
      </div>

      {loading ? (
        <div className="d-flex justify-content-center py-5">
          <div className="spinner-border text-light" role="status">
            <span className="visually-hidden">Loading draft...</span>
          </div>
        </div>
      ) : activePageTab === "history" ? (
        <div className="tool-defs-card tool-defs-history-card">
          <div className="tool-defs-card-header">
            <label className="tool-defs-label">Version history</label>
            {historyLoading ? (
              <span className="tool-defs-status tool-defs-status--saving">Loading...</span>
            ) : null}
          </div>

          {historyErrorMessage ? (
            <div className="alert alert-danger py-2 mb-3">{historyErrorMessage}</div>
          ) : null}

          {!historyLoading && historyVersions.length === 0 ? (
            <div className="tool-defs-empty">
              Nothing has been published yet for this practice. Publish a bundle to start the
              history.
            </div>
          ) : (
            <ul className="tool-defs-history-list">
              {historyVersions.map((version) => (
                <li key={version.versionId} className="tool-defs-history-item">
                  <div className="tool-defs-history-item-main">
                    <div className="tool-defs-history-item-heading">
                      <span className="tool-defs-history-version">
                        Version {version.versionId}
                      </span>
                      {version.versionId === mostRecentPublishedVersionId ? (
                        <span className="tool-defs-history-recent-badge">MOST RECENT</span>
                      ) : null}
                    </div>
                    <div className="tool-defs-history-timestamp">
                      Published {formatSavedAt(version.publishedAt) || version.publishedAt}
                    </div>
                    <div className="tool-defs-history-preview">
                      {summarizeBundleForHistory(version.bundle)}
                    </div>
                  </div>
                  <button
                    type="button"
                    className="btn btn-outline-light tool-defs-history-rollback-btn"
                    onClick={() => handleRequestRollback(version)}
                    disabled={version.versionId === mostRecentPublishedVersionId}
                    title={
                      version.versionId === mostRecentPublishedVersionId
                        ? "This is already the most recently published version"
                        : "Roll back to this version"
                    }
                  >
                    Rollback
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : (
        <div className="tool-defs-card">
          <div className="tool-defs-card-header">
            <label className="tool-defs-label">Tool definitions ({tools.length})</label>
            <span className={`tool-defs-status ${statusMeta.className}`}>
              {statusMeta.label}
            </span>
          </div>

          <div className="tool-defs-toolbar">
            <button type="button" className="btn btn-outline-light" onClick={openAddToolModal}>
              + Add Tool
            </button>
          </div>

          {tools.length === 0 ? (
            <div className="tool-defs-empty">
              No tools yet. Add an API call, cold transfer, or warm transfer to get started.
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table align-middle tool-defs-table">
                <thead>
                  <tr>
                    <th scope="col">Name</th>
                    <th scope="col">Type</th>
                    <th scope="col">Summary</th>
                    <th scope="col">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {tools.map((tool) => (
                    <tr key={tool.id}>
                      <td>{tool.name || <em>(unnamed)</em>}</td>
                      <td>{FUNC_TYPE_LABELS[tool.funcType]}</td>
                      <td className="tool-defs-summary-cell">
                        {tool.funcType === "apiFunction"
                          ? tool.url || "(no URL set)"
                          : `${tool.handoverPhoneNumber || "(no number set)"}${
                              tool.extension ? ` ext. ${tool.extension}` : ""
                            }`}
                      </td>
                      <td>
                        <div className="d-flex align-items-center gap-3">
                          <button
                            type="button"
                            className="btn btn-link tool-defs-inline-action"
                            onClick={() => openEditToolModal(tool)}
                          >
                            Edit
                          </button>
                          <button
                            type="button"
                            className="btn btn-link tool-defs-inline-action tool-defs-inline-action--danger"
                            onClick={() => handleRemoveTool(tool.id)}
                          >
                            Remove
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {errorMessage ? <div className="alert alert-danger py-2 mt-3 mb-0">{errorMessage}</div> : null}
          {submitErrorMessage ? (
            <div className="alert alert-danger py-2 mt-3 mb-0">{submitErrorMessage}</div>
          ) : null}

          <div className="tool-defs-actions">
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleSaveNow}
              disabled={saveState === "saving"}
            >
              Save Draft
            </button>
            <button
              type="button"
              className="btn btn-outline-light"
              onClick={handleSubmitDraft}
              disabled={submitState === "submitting"}
            >
              {submitState === "submitting" ? "Submitting..." : "Submit Draft"}
            </button>
            <button
              type="button"
              className={`tool-defs-publish-btn ${
                publishEnabled ? "tool-defs-publish-btn--enabled" : "tool-defs-publish-btn--disabled"
              }`}
              onClick={handlePublishClick}
              disabled={!publishEnabled}
              title={publishTitle}
            >
              {publishState === "publishing" ? "Publishing..." : "Publish"}
            </button>
          </div>

          <div className="tool-defs-submit-hint">
            {!hasSubmission
              ? "Submit a draft to enable Publish."
              : isDirtySinceSubmit
                ? "Draft has changed since the last submit - submit again before publishing the latest content."
                : `Submitted draft matches the editor${
                    lastSubmittedAtLabel ? ` - submitted ${lastSubmittedAtLabel}` : ""
                  }.`}
          </div>

          {publishMessage ? <div className="tool-defs-note">{publishMessage}</div> : null}
        </div>
      )}

      {showToolModal ? (
        <div className="tool-defs-backdrop" onClick={closeToolModal}>
          <div
            className="tool-defs-modal"
            role="dialog"
            aria-label={editingToolId ? "Edit tool" : "Add tool"}
            onClick={(event) => event.stopPropagation()}
          >
            <h3 className="tool-defs-modal-title">
              {editingToolId ? "Edit tool" : "Add tool"}
            </h3>

            <label className="tool-defs-label" htmlFor="toolDefsName">
              Name
            </label>
            <input
              id="toolDefsName"
              type="text"
              className="tool-defs-input"
              value={toolDraft.name}
              onChange={(event) => handleToolFieldChange("name", event.target.value)}
              placeholder="e.g. GetPatientRecord"
            />

            <label className="tool-defs-label tool-defs-label--spaced" htmlFor="toolDefsType">
              Type
            </label>
            <select
              id="toolDefsType"
              className="tool-defs-input"
              value={toolDraft.funcType}
              onChange={(event) => handleToolFieldChange("funcType", event.target.value)}
            >
              {FUNC_TYPE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>

            <label className="tool-defs-label tool-defs-label--spaced" htmlFor="toolDefsDescription">
              Description
            </label>
            <textarea
              id="toolDefsDescription"
              className="tool-defs-textarea tool-defs-textarea--short"
              value={toolDraft.description}
              onChange={(event) => handleToolFieldChange("description", event.target.value)}
              placeholder="What this tool does, for the agent to decide when to call it..."
            />

            {toolDraft.funcType === "apiFunction" ? (
              <>
                <label className="tool-defs-label tool-defs-label--spaced" htmlFor="toolDefsUrl">
                  URL
                </label>
                <input
                  id="toolDefsUrl"
                  type="text"
                  className="tool-defs-input"
                  value={toolDraft.url}
                  onChange={(event) => handleToolFieldChange("url", event.target.value)}
                  placeholder="https://api.example.com/patients"
                />

                <div className="tool-defs-field-row">
                  <div className="tool-defs-field-col">
                    <label className="tool-defs-label" htmlFor="toolDefsMethod">
                      Method
                    </label>
                    <select
                      id="toolDefsMethod"
                      className="tool-defs-input"
                      value={toolDraft.method}
                      onChange={(event) => handleToolFieldChange("method", event.target.value)}
                    >
                      {["GET", "POST", "PATCH", "PUT", "DELETE"].map((method) => (
                        <option key={method} value={method}>
                          {method}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="tool-defs-field-col">
                    <label className="tool-defs-label" htmlFor="toolDefsExecutionMode">
                      Call type
                    </label>
                    <select
                      id="toolDefsExecutionMode"
                      className="tool-defs-input"
                      value={toolDraft.executionMode}
                      onChange={(event) =>
                        handleToolFieldChange("executionMode", event.target.value)
                      }
                    >
                      <option value="standard">Standard</option>
                      <option value="stateful">Stateful</option>
                    </select>
                  </div>
                </div>

                <label className="tool-defs-label tool-defs-label--spaced" htmlFor="toolDefsAuthHeader">
                  Auth header
                </label>
                <input
                  id="toolDefsAuthHeader"
                  type="text"
                  className="tool-defs-input"
                  value={toolDraft.authHeader}
                  onChange={(event) => handleToolFieldChange("authHeader", event.target.value)}
                  placeholder="Bearer ..."
                />

                <div className="tool-defs-field-row">
                  <div className="tool-defs-field-col">
                    <label className="tool-defs-label" htmlFor="toolDefsTimeout">
                      Timeout (sec)
                    </label>
                    <input
                      id="toolDefsTimeout"
                      type="number"
                      min="0"
                      className="tool-defs-input"
                      value={toolDraft.timeoutSec}
                      onChange={(event) =>
                        handleToolFieldChange("timeoutSec", Number(event.target.value))
                      }
                    />
                  </div>
                  <div className="tool-defs-field-col">
                    <label className="tool-defs-label" htmlFor="toolDefsRetry">
                      Retry count
                    </label>
                    <input
                      id="toolDefsRetry"
                      type="number"
                      min="0"
                      className="tool-defs-input"
                      value={toolDraft.retryCount}
                      onChange={(event) =>
                        handleToolFieldChange("retryCount", Number(event.target.value))
                      }
                    />
                  </div>
                </div>

                <div className="tool-defs-subsection">
                  <div className="tool-defs-subsection-header">
                    <span className="tool-defs-label">Attributes</span>
                    <button
                      type="button"
                      className="btn btn-link tool-defs-inline-action"
                      onClick={handleAddAttribute}
                    >
                      + Add attribute
                    </button>
                  </div>
                  {toolDraft.attributes.map((attr, index) => (
                    <div className="tool-defs-repeatable-row" key={`attr-${index}`}>
                      <input
                        type="text"
                        className="tool-defs-input tool-defs-input--sm"
                        placeholder="name"
                        value={attr.name}
                        onChange={(event) =>
                          handleAttributeChange(index, "name", event.target.value)
                        }
                      />
                      <select
                        className="tool-defs-input tool-defs-input--sm"
                        value={attr.attrType}
                        onChange={(event) =>
                          handleAttributeChange(index, "attrType", event.target.value)
                        }
                      >
                        <option value="string">string</option>
                        <option value="number">number</option>
                        <option value="boolean">boolean</option>
                      </select>
                      <label className="tool-defs-checkbox-label">
                        <input
                          type="checkbox"
                          checked={attr.required}
                          onChange={(event) =>
                            handleAttributeChange(index, "required", event.target.checked)
                          }
                        />
                        required
                      </label>
                      <input
                        type="text"
                        className="tool-defs-input tool-defs-input--sm"
                        placeholder="default value"
                        value={attr.defaultValue}
                        onChange={(event) =>
                          handleAttributeChange(index, "defaultValue", event.target.value)
                        }
                      />
                      <button
                        type="button"
                        className="btn btn-link tool-defs-inline-action tool-defs-inline-action--danger"
                        onClick={() => handleRemoveAttribute(index)}
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                </div>

                <div className="tool-defs-subsection">
                  <div className="tool-defs-subsection-header">
                    <span className="tool-defs-label">Routing rules</span>
                    <button
                      type="button"
                      className="btn btn-link tool-defs-inline-action"
                      onClick={handleAddRoutingRule}
                    >
                      + Add routing rule
                    </button>
                  </div>
                  {toolDraft.routingRules.map((rule, index) => (
                    <div className="tool-defs-repeatable-row" key={`rule-${index}`}>
                      <input
                        type="text"
                        className="tool-defs-input tool-defs-input--sm"
                        placeholder="field"
                        value={rule.field}
                        onChange={(event) =>
                          handleRoutingRuleChange(index, "field", event.target.value)
                        }
                      />
                      <input
                        type="text"
                        className="tool-defs-input tool-defs-input--sm"
                        placeholder="min"
                        value={rule.min}
                        onChange={(event) =>
                          handleRoutingRuleChange(index, "min", event.target.value)
                        }
                      />
                      <input
                        type="text"
                        className="tool-defs-input tool-defs-input--sm"
                        placeholder="max"
                        value={rule.max}
                        onChange={(event) =>
                          handleRoutingRuleChange(index, "max", event.target.value)
                        }
                      />
                      <input
                        type="text"
                        className="tool-defs-input tool-defs-input--sm"
                        placeholder="target URL"
                        value={rule.targetUrl}
                        onChange={(event) =>
                          handleRoutingRuleChange(index, "targetUrl", event.target.value)
                        }
                      />
                      <input
                        type="text"
                        className="tool-defs-input tool-defs-input--sm"
                        placeholder="target auth header"
                        value={rule.targetAuthHeader}
                        onChange={(event) =>
                          handleRoutingRuleChange(index, "targetAuthHeader", event.target.value)
                        }
                      />
                      <button
                        type="button"
                        className="btn btn-link tool-defs-inline-action tool-defs-inline-action--danger"
                        onClick={() => handleRemoveRoutingRule(index)}
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <>
                <label
                  className="tool-defs-label tool-defs-label--spaced"
                  htmlFor="toolDefsHandoverNumber"
                >
                  Handover phone number
                </label>
                <input
                  id="toolDefsHandoverNumber"
                  type="text"
                  className="tool-defs-input"
                  value={toolDraft.handoverPhoneNumber}
                  onChange={(event) =>
                    handleToolFieldChange("handoverPhoneNumber", event.target.value)
                  }
                  placeholder="+15551234567"
                />

                <div className="tool-defs-field-row">
                  <div className="tool-defs-field-col">
                    <label className="tool-defs-label" htmlFor="toolDefsExtension">
                      Extension
                    </label>
                    <input
                      id="toolDefsExtension"
                      type="text"
                      className="tool-defs-input"
                      value={toolDraft.extension}
                      onChange={(event) => handleToolFieldChange("extension", event.target.value)}
                    />
                  </div>
                  <div className="tool-defs-field-col">
                    <label className="tool-defs-label" htmlFor="toolDefsTransferLine">
                      Transfer line
                    </label>
                    <input
                      id="toolDefsTransferLine"
                      type="text"
                      className="tool-defs-input"
                      value={toolDraft.transferLine}
                      onChange={(event) =>
                        handleToolFieldChange("transferLine", event.target.value)
                      }
                    />
                  </div>
                </div>

                {toolDraft.funcType === "warm_transfer" ? (
                  <>
                    <label
                      className="tool-defs-label tool-defs-label--spaced"
                      htmlFor="toolDefsWarmSummaryPrompt"
                    >
                      Warm summary prompt
                    </label>
                    <textarea
                      id="toolDefsWarmSummaryPrompt"
                      className="tool-defs-textarea tool-defs-textarea--short"
                      value={toolDraft.warmSummaryPrompt}
                      onChange={(event) =>
                        handleToolFieldChange("warmSummaryPrompt", event.target.value)
                      }
                      placeholder="What the agent should tell the receiving line before transferring..."
                    />
                  </>
                ) : null}
              </>
            )}

            {toolModalError ? (
              <div className="alert alert-danger py-2 mt-3 mb-0">{toolModalError}</div>
            ) : null}

            <div className="tool-defs-modal-actions">
              <button type="button" className="btn btn-outline-light" onClick={closeToolModal}>
                Cancel
              </button>
              <button type="button" className="btn btn-primary" onClick={handleSaveToolFromModal}>
                {editingToolId ? "Save changes" : "Add tool"}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {rollbackTargetVersion ? (
        <div className="tool-defs-backdrop" onClick={handleCancelRollback}>
          <div
            className="tool-defs-confirm-dialog"
            role="dialog"
            aria-label="Confirm rollback"
            onClick={(event) => event.stopPropagation()}
          >
            <h3 className="tool-defs-modal-title">Roll back to this version?</h3>
            <p className="tool-defs-confirm-body">
              This creates a new published version using version{" "}
              {rollbackTargetVersion.versionId}&apos;s content (published{" "}
              {formatSavedAt(rollbackTargetVersion.publishedAt) ||
                rollbackTargetVersion.publishedAt}
              ). The version you&apos;re rolling back from stays in history - nothing is deleted.
            </p>
            <div className="tool-defs-confirm-preview">
              {summarizeBundleForHistory(rollbackTargetVersion.bundle)}
            </div>

            {rollbackErrorMessage ? (
              <div className="alert alert-danger py-2 mb-0">{rollbackErrorMessage}</div>
            ) : null}

            <div className="tool-defs-confirm-actions">
              <button
                type="button"
                className="btn btn-outline-light"
                onClick={handleCancelRollback}
                disabled={rollbackState === "rolling-back"}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleConfirmRollback}
                disabled={rollbackState === "rolling-back"}
              >
                {rollbackState === "rolling-back" ? "Rolling back..." : "Confirm rollback"}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};
